import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Divider,
  Avatar,
  Chip,
  LinearProgress,
  CircularProgress,
  Skeleton,
  useTheme,
  Button,
  Paper,
  alpha,
  Stack,
  Grid,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { useClients } from "../../hooks/useClients";
import { useClientsGroups, useUpdateClientStage } from "../../hooks/useClientsGroups";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import PsychologyOutlinedIcon from "@mui/icons-material/PsychologyOutlined";
import TrendingUpOutlinedIcon from "@mui/icons-material/TrendingUpOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { useTranslation } from "react-i18next";

const EXPLICACAO_NIVEIS_CONSCIENCIA =
  "Os níveis de consciência são etapas que representam em que estágio do relacionamento ou da jornada de compra o cliente se encontra — por exemplo: ainda não conhece o problema, está considerando soluções ou já decidiu comprar. Eles ajudam a organizar o pipeline de clientes e o atendimento. Para usar esta tela, cadastre primeiro as etapas (níveis) na tela de cadastro.";

const Pipeline = () => {
  const theme = useTheme();
  const { t } = useTranslation("settings");
  const navigate = useNavigate();
  const { data: clientsData, isLoading, error, refetch } = useClients();
  const { data: pipelineStages, isLoading: isLoadingStages, error: errorStages } = useClientsGroups();
  const updateClientStage = useUpdateClientStage();
  const [pipeline, setPipeline] = useState({});

  useEffect(() => {
    if (!clientsData || !pipelineStages) return;

    const formattedData = pipelineStages.reduce((acc, stage) => {
      acc[String(stage.id)] = clientsData?.clients?.filter(client => client.id_grupo === stage.id) || [];
      return acc;
    }, {});

    setPipeline(formattedData ?? {}); // Evita undefined
  }, [clientsData, pipelineStages]);

  const onDragEnd = useCallback(
    (result) => {
      if (!result.destination) return;

      const { source, destination } = result;
      const sourceStage = source.droppableId;
      const destStage = destination.droppableId;

      if (!pipeline[sourceStage] || !pipeline[destStage]) return;

      const newPipeline = { ...pipeline };
      const [movedCard] = newPipeline[sourceStage].splice(source.index, 1);
      newPipeline[destStage].splice(destination.index, 0, movedCard);

      setPipeline(newPipeline);

      const newStage = pipelineStages?.find(stage => String(stage.id) === destStage);
      if (newStage) {
        updateClientStage.mutate(
          { clientId: movedCard.id, newStageId: newStage.id },
          {
            onError: () => {
              console.error("Erro ao atualizar estágio do cliente");
              refetch();
            }
          }
        );
      }
    },
    [pipeline, pipelineStages, updateClientStage, refetch]
  );

  if (isLoading || isLoadingStages) {
    return (
      <Box className="tour-pipeline-root" sx={{ p: 3, backgroundColor: theme.palette.background.default }}>
        <Skeleton variant="text" width="40%" height={48} sx={{ mb: 1 }} />
        <Skeleton variant="text" width="70%" height={28} sx={{ mb: 3 }} />
        <Divider sx={{ mb: 3, backgroundColor: theme.palette.divider }} />
        <Box sx={{ display: "flex", overflowX: "auto", gap: 2 }}>
          {[1, 2, 3, 4].map((col) => (
            <Box
              key={col}
              sx={{
                minWidth: 300,
                flex: "0 0 300px",
                borderRadius: 2,
                border: 1,
                borderColor: "divider",
                p: 2,
                bgcolor: "background.paper",
              }}
            >
              <Skeleton variant="text" width="70%" height={32} sx={{ mb: 2 }} />
              <Skeleton variant="text" width="100%" height={20} sx={{ mb: 2 }} />
              <Divider sx={{ mb: 2 }} />
              <Skeleton variant="rounded" width="100%" height={8} sx={{ mb: 2, borderRadius: 1 }} />
              <Skeleton variant="rounded" width="100%" height={100} sx={{ mb: 1.5, borderRadius: 1 }} />
              <Skeleton variant="rounded" width="100%" height={100} sx={{ mb: 1.5, borderRadius: 1 }} />
              <Skeleton variant="rounded" width="100%" height={100} sx={{ borderRadius: 1 }} />
            </Box>
          ))}
        </Box>
      </Box>
    );
  }

  if (error || errorStages) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
        <Typography color="error">Erro ao carregar dados. Tente novamente.</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        p: 3,
        backgroundColor: theme.palette.background.default
      }}>
      <Typography variant="h4" gutterBottom color={theme.palette.text.primary}>
        {t("pipelinePage.awarenessHeading")}
      </Typography>
      <Typography
        variant="body1"
        color={theme.palette.text.secondary}
        gutterBottom
      >
        {t("pipelinePage.awarenessSubtitle")}
      </Typography>
      <Divider sx={{ mb: 3, backgroundColor: theme.palette.divider }} />
      <DragDropContext onDragEnd={onDragEnd}>
        <Box className="tour-pipeline-board" sx={{ display: "flex", overflowX: "auto", gap: 2, padding: 3, width: "100%" }}>
          {pipelineStages?.length ? (
            pipelineStages.map((stage) => (
              <PipelineStage
                key={String(stage.id)}
                stage={stage}
                clients={pipeline[String(stage.id)] ?? []}
              />
            ))
          ) : (
            <Paper
              variant="outlined"
              sx={{
                width: "100%",
                maxWidth: 960,
                mx: "auto",
                p: { xs: 3, sm: 4 },
                borderRadius: 3,
                border: `1px solid ${alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.25 : 0.18)}`,
                background: theme.palette.mode === "dark"
                  ? `linear-gradient(160deg, ${alpha(theme.palette.background.paper, 0.98)} 0%, ${alpha(theme.palette.primary.main, 0.06)} 50%, ${alpha(theme.palette.background.paper, 0.98)} 100%)`
                  : `linear-gradient(160deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.primary.main, 0.06)} 40%, ${theme.palette.background.paper} 100%)`,
                boxShadow: theme.palette.mode === "dark"
                  ? `0 12px 40px ${alpha(theme.palette.common.black, 0.35)}`
                  : `0 12px 40px ${alpha(theme.palette.primary.main, 0.1)}`,
              }}
            >
              <Grid container spacing={4} alignItems="center">
                <Grid item xs={12} md={8}>
                  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, mb: 2 }}>
                    <Box
                      sx={{
                        position: "relative",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 56,
                        height: 56,
                        flexShrink: 0,
                        borderRadius: "50%",
                        bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.2 : 0.12),
                      }}
                    >
                      <LightbulbOutlinedIcon sx={{ fontSize: 32 }} color="primary" />
                    </Box>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: theme.palette.text.primary }}>
                        Nenhum nível de consciência cadastrado
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1, lineHeight: 1.75 }}>
                        {EXPLICACAO_NIVEIS_CONSCIENCIA}
                      </Typography>
                    </Box>
                  </Box>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <Chip size="small" icon={<PsychologyOutlinedIcon sx={{ fontSize: 18 }} />} label="Jornada do cliente" variant="outlined" sx={{ "& .MuiChip-icon": { color: "primary.main" } }} />
                    <Chip size="small" icon={<TrendingUpOutlinedIcon sx={{ fontSize: 18 }} />} label="Pipeline organizado" variant="outlined" sx={{ "& .MuiChip-icon": { color: "primary.main" } }} />
                  </Stack>
                </Grid>
                <Grid item xs={12} md={4} sx={{ display: "flex", justifyContent: { xs: "flex-start", md: "flex-end" } }}>
                  <Button
                    variant="contained"
                    size="large"
                    endIcon={<ArrowForwardIcon />}
                    onClick={() => navigate("/awareness-stages")}
                    sx={{
                      py: 1.5,
                      px: 3,
                      fontWeight: 600,
                      borderRadius: 2,
                      boxShadow: 2,
                      "&:hover": { boxShadow: 4 },
                    }}
                  >
                    Ir para cadastro de níveis de consciência
                  </Button>
                </Grid>
              </Grid>
            </Paper>
          )}
        </Box>
      </DragDropContext>
    </Box>
  );
};

const PipelineStage = ({ stage, clients }) => {
  const theme = useTheme();
  return (
    <Droppable droppableId={String(stage.id)}>
      {(provided) => (
        <Box
          ref={provided.innerRef}
          {...provided.droppableProps}
          sx={{
            flex: "0 0 300px",
            backgroundColor: theme.palette.background.default,
            borderRadius: 2,
            boxShadow: "0px 4px 6px rgba(0,0,0,0.1)",
            padding: 2,
            minHeight: "450px",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: "bold", color: theme.palette.primary.main, mb: 2 }}>
            {stage.nome} ({clients?.length ?? 0})
          </Typography>
          <Typography variant="body2" color={theme.palette.text.secondary} sx={{ mb: 2 }}>
            {stage.description}
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <LinearProgress
            variant="determinate"
            value={(clients?.length / 10) * 100 || 0}
            sx={{ height: 8, borderRadius: 5, mb: 2 }}
          />
          <Box sx={{ flex: 1, overflowY: "auto" }}>
            {Array.isArray(clients) && clients.length > 0 ? (
              clients.map((client, index) => <PipelineCard key={String(client.id)} client={client} index={index} />)
            ) : (
              <Typography variant="body2" color={theme.palette.text.secondary} sx={{ textAlign: "center" }}>
                Nenhum cliente neste estágio.
              </Typography>
            )}
            {provided.placeholder}
          </Box>
        </Box>
      )}
    </Droppable>
  );
};

const PipelineCard = ({ client, index }) => {
  const theme = useTheme();

  // Obtém as iniciais do nome do cliente
  const getInitials = (name) => {
    if (!name) return "?";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase();
  };

  return (
    <Draggable key={String(client.id)} draggableId={String(client.id)} index={index}>
      {(provided) => (
        <Card
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          sx={{
            mb: 2,
            backgroundColor: theme.palette.background.paper,
            border: `1px solid ${theme.palette.divider}`,
            padding: 1,
            borderRadius: 2,
            boxShadow: "0px 2px 4px rgba(0,0,0,0.1)",
            cursor: "grab", 
          }}
        >
          <CardContent>
            <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
              <Avatar
                src={client?.imagem || undefined}
                sx={{ bgcolor: theme.palette.primary.main, color: "#fff", mr: 2 }}
              >
                {!client?.imagem && getInitials(client.nome)}
              </Avatar>
              <Box>
                <Typography variant="subtitle1" fontWeight="bold" color={theme.palette.text.primary}>
                  {client.nome ?? "Nome não disponível"}
                </Typography>
                <Typography variant="body2" color={theme.palette.text.secondary}>
                  {client.email ?? "Sem e-mail"}
                </Typography>
              </Box>
            </Box>

            <Divider sx={{ mb: 1 }} />

            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
              <Chip
                label={client.priority ?? "Normal"}
                color={client.priority === "Alta" ? "error" : "primary"}
                size="small"
              />
              {client.telefone && (
                <Typography variant="body2" color={theme.palette.text.secondary}>
                  📞 {client.telefone}
                </Typography>
              )}
            </Box>

            {client.status && (
              <Typography variant="body2" fontWeight="bold" color={theme.palette.success.main} mb={1}>
                {client.status}
              </Typography>
            )}

            {client.data_cadastro && (
              <Typography variant="caption" color={theme.palette.text.secondary}>
                Cadastrado em: {new Date(client.data_cadastro).toLocaleDateString()}
              </Typography>
            )}
          </CardContent>
        </Card>
      )}
    </Draggable>
    
  );
};


export default Pipeline;
